import React from "react";
import { Box, Slider, Stack, Typography } from "@mui/material";
import { InlineNumber } from "./inputs";

// Escala de edades de las plataformas de anuncios: de 13 a 65+. Una edad
// guardada fuera de esa escala la amplía, para no recortarla al editar.
const SCALE_MIN = 13;
const SCALE_MAX = 65;

const toAge = (value: string): number | null => {
  const age = Number(value);
  return value.trim() && !Number.isNaN(age) ? age : null;
};

const scaleFor = (min: number, max: number) => ({
  from: Math.min(SCALE_MIN, min),
  to: Math.max(SCALE_MAX, max),
});

const scaleLabel = (age: number) => (age === SCALE_MAX ? `${age}+` : `${age}`);

/** Vista: una barra de 13 a 65+ con el tramo de edad marcado y sus dos topes. */
export const AgeRangeTrack: React.FC<{ min: string; max: string }> = ({
  min,
  max,
}) => {
  const minAge = toAge(min);
  const maxAge = toAge(max);
  if (minAge === null || maxAge === null) {
    return <>{[min, max].filter(Boolean).join(" – ") || "—"}</>;
  }
  const { from, to } = scaleFor(minAge, maxAge);
  const at = (age: number) => `${((age - from) / (to - from)) * 100}%`;

  const thumb = (age: number) => (
    <Box
      sx={{
        position: "absolute",
        left: at(age),
        top: 0,
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, color: "text.primary" }}
      >
        {age}
      </Typography>
      <Box
        sx={{
          mt: 0.75,
          width: 14,
          height: 14,
          borderRadius: "50%",
          bgcolor: "background.paper",
          border: "2px solid",
          borderColor: "primary.main",
          boxShadow: 1,
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ px: 1, pt: 0.5 }}>
      <Box sx={{ position: "relative", height: 34 }}>
        {/* Escala completa y tramo de la línea, a la altura del centro de los topes */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 25,
            height: 4,
            borderRadius: 2,
            bgcolor: "divider",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: at(minAge),
            width: `calc(${at(maxAge)} - ${at(minAge)})`,
            top: 25,
            height: 4,
            borderRadius: 2,
            bgcolor: "primary.main",
          }}
        />
        {thumb(minAge)}
        {thumb(maxAge)}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 0.5, fontSize: 11, color: "text.disabled" }}
      >
        <span>{scaleLabel(from)}</span>
        <span>{scaleLabel(to)}</span>
      </Stack>
    </Box>
  );
};

/** Edición: el mismo tramo con un slider de dos topes y, debajo, las dos edades a mano. */
export const AgeRangeInput: React.FC<{
  min: string;
  max: string;
  onChange: (min: string, max: string) => void;
  minLabel: string;
  maxLabel: string;
  minError?: boolean;
  maxError?: boolean;
  autoFocus?: boolean;
}> = ({ min, max, onChange, minLabel, maxLabel, minError, maxError, autoFocus }) => {
  const minAge = toAge(min);
  const maxAge = toAge(max);
  // Sin edades todavía (línea nueva), el slider parte de 18–65 sin tocar el borrador.
  const value: [number, number] = [minAge ?? 18, maxAge ?? SCALE_MAX];
  const { from, to } = scaleFor(value[0], value[1]);

  return (
    <Box>
      <Box sx={{ px: 1.5 }}>
        <Slider
          size="small"
          value={value}
          min={from}
          max={to}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={scaleLabel}
          getAriaLabel={(index) => (index === 0 ? minLabel : maxLabel)}
          onChange={(_, next) => {
            const [nextMin, nextMax] = next as number[];
            onChange(String(nextMin), String(nextMax));
          }}
          marks={[
            { value: from, label: scaleLabel(from) },
            { value: to, label: scaleLabel(to) },
          ]}
          sx={{ "& .MuiSlider-markLabel": { fontSize: 11, color: "text.disabled" } }}
        />
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <InlineNumber
          value={min}
          placeholder={minLabel}
          inputProps={{ "aria-label": minLabel }}
          error={minError}
          autoFocus={autoFocus}
          onChange={(event) => onChange(event.target.value, max)}
        />
        <Typography color="text.secondary">–</Typography>
        <InlineNumber
          value={max}
          placeholder={maxLabel}
          inputProps={{ "aria-label": maxLabel }}
          error={maxError}
          onChange={(event) => onChange(min, event.target.value)}
        />
      </Stack>
    </Box>
  );
};
